import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { DateTime } from 'luxon'
import {
  findAppointments,
  Appointment as AppointmentAPI,
  FindAppointmentsResponse,
  Screens,
  findClinicsByIds,
  FindClinicsByIdsResponse,
} from 'patient-api-lib'
import { AppDispatch, RootState } from '../../store'
import { Appointment, Clinic } from '../../models/types'
import {
  createDateFromObject,
  getLocale,
  shiftClientTimeZone,
} from '../../util/date'
import { capitalizeFirstLetter } from '../../util/capitalizeFirstLetter'
import { getTreatmentIdsFromTreatments } from '../treatmentScreen'
import { adaptAppointmentResponse } from './appointmentUtils'

interface AppointmentScreenState {
  pending: boolean
  pageToken: string
  pageSize: number
  appointments: Appointment[]
  selectedAppointment: Appointment | null
  date: string | null
  upcomingAppointmentsDate: string | null
}

export const initialState: AppointmentScreenState = {
  pending: false,
  pageToken: '1',
  pageSize: 20,
  appointments: [],
  selectedAppointment: null,
  date: null,
  upcomingAppointmentsDate: null,
}

export const setNearestAppointmentDate = createAsyncThunk<
  void,
  Clinic | null,
  { dispatch: AppDispatch; state: RootState }
>('appointmentScreen/setNearestAppointmentDate', async (clinic, thunkApi) => {
  const state = thunkApi.getState()
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const selectedClinic = clinic ? clinic : state.clinicScreen.selectedClinic
  const treatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)

  const appointmentsWithinTreatments =
    selectedClinic?.upcomingAppointments?.list?.filter((item) =>
      treatmentIds.includes(item.treatmentId)
    )

  const nearestAvailableTime = appointmentsWithinTreatments?.reduce(
    (
      prev: AppointmentAPI.AsObject | null,
      current: AppointmentAPI.AsObject | null
    ) => {
      if (!current) return null
      if (!prev) return current

      const prevDate = createDateFromObject(prev.startTime).toJSDate()
      const currentDate = createDateFromObject(current.startTime).toJSDate()

      if (prevDate < currentDate) return prev

      return current
    },
    null
  )

  thunkApi.dispatch(
    setDate(createDateFromObject(nearestAvailableTime?.startTime).toISO())
  )
})

export const fetchAppointments = createAsyncThunk<
  Appointment[],
  { pageToken?: string; pageSize?: number } | undefined
>('appointmentScreen/fetchAppointments', async (payload, thunkApi) => {
  const state: RootState = thunkApi.getState() as RootState
  const selectedClinic = state.clinicScreen.selectedClinic

  const date =
    state.appointmentScreen.date ||
    selectedClinic?.upcomingAppointments?.date ||
    new Date().toISOString()
  if (!selectedClinic) return []

  const startDate = DateTime.fromISO(date).setZone('system', {
    keepLocalTime: true,
  })
  const endDate = startDate.endOf('day')
  const selectedTreatment = state.treatmentScreen.selectedTreatment
  const treatmentIds = getTreatmentIdsFromTreatments(selectedTreatment)
  const clinicIds = [selectedClinic.id]
  const pageToken = payload?.pageToken || state.appointmentScreen.pageToken
  const pageSize = state.appointmentScreen.pageSize

  let appointments: Appointment[] = []

  const getAppointments = async (
    pageToken: string | undefined,
    pageSize: number
  ): Promise<FindAppointmentsResponse> => {
    const response = await findAppointments(
      startDate.toJSDate(),
      endDate.toJSDate(),
      treatmentIds,
      clinicIds,
      pageToken,
      pageSize
    )

    if (!state.appointmentScreen.upcomingAppointmentsDate)
      thunkApi.dispatch(
        setUpcomingAppointmentsDate(
          selectedClinic?.upcomingAppointments?.date || null
        )
      )

    thunkApi.dispatch(setDate(startDate.toISO({ includeOffset: false })))

    appointments = [
      ...appointments,
      ...response.getAppointmentsList().map(adaptAppointmentResponse),
    ]

    if (response.getNextPageToken()) {
      await getAppointments(response.getNextPageToken()?.getValue(), pageSize)
    }

    return response
  }

  const findClinicsResponse = await findClinicsByIds(clinicIds)
  await getAppointments(pageToken, pageSize)
  appointments = await Promise.all(
    appointments.map(async (appointment) => {
      const clinic: FindClinicsByIdsResponse.ClinicInfo | undefined =
        findClinicsResponse
          ?.getClinicsList()
          .find((clinic) => clinic.getId() === appointment.clinicId)

      if (!clinic) return appointment

      const careGiverImage: string | undefined = clinic
        ?.getCaregiversList()
        ?.find((caregiver) => {
          return caregiver.getId() === appointment.careGiverId
        })
        ?.getImageUrl()
        ?.getValue()

      const newAppointment = {
        ...appointment,
        careGiverImage,
      }

      return newAppointment
    })
  )

  return appointments
})

export const goToDay = createAsyncThunk<
  void,
  Date,
  { dispatch: AppDispatch; state: RootState }
>('appointmentsScreen/selectDate', async (date, thunkApi) => {
  const ISOClientZoneLess = shiftClientTimeZone(date)

  const dateObjToDateTime = DateTime.fromISO(ISOClientZoneLess)
  const newDate = dateObjToDateTime.startOf('day')

  thunkApi.dispatch(setDate(newDate.toISO()))
  thunkApi.dispatch(fetchAppointments())
})

export const goToNextDay = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('appointmentsScreen/selectNextDate', async (_, thunkApi) => {
  const date = thunkApi.getState().appointmentScreen.date
  if (!date) return

  const newDate = DateTime.fromISO(date).plus({ day: 1 }).startOf('day')
  thunkApi.dispatch(setDate(newDate.toISO({ includeOffset: false })))
  thunkApi.dispatch(fetchAppointments())
})

export const goToPreviousDay = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('appointmentsScreen/selectPeviousDate', async (_, thunkApi) => {
  const date = thunkApi.getState().appointmentScreen.date
  const currentDate = DateTime.now()
  if (!date) return

  const newDate = DateTime.fromISO(date).minus({ day: 1 }).startOf('day')
  if (newDate <= currentDate) {
    thunkApi.dispatch(setDate(currentDate.toISO({ includeOffset: false })))

    if (!currentDate.hasSame(newDate, 'day')) return
    thunkApi.dispatch(fetchAppointments())
    return
  }

  thunkApi.dispatch(setDate(newDate.toISO({ includeOffset: false })))
  thunkApi.dispatch(fetchAppointments())
})

export const done = createAsyncThunk('appointmentScreen/done', async () => {
  return Screens.BookingSummaryScreen
})

export const AppointmentScreenSlice = createSlice({
  name: 'appointmentScreen',
  initialState,
  reducers: {
    setDate: (state, action: PayloadAction<string | null>) => {
      state.date = action.payload
    },
    setUpcomingAppointmentsDate: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.upcomingAppointmentsDate = action.payload
    },
    setSelectedAppointment: (
      state,
      action: PayloadAction<Appointment | null>
    ) => {
      state.selectedAppointment = action.payload
    },
    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAppointments.fulfilled, (state, action) => {
      state.appointments = action.payload
      state.pending = false
    })
    builder.addCase(fetchAppointments.pending, (state) => {
      state.pending = true
    })
  },
})

export const {
  setDate,
  setSelectedAppointment,
  setUpcomingAppointmentsDate,
  clearState,
} = AppointmentScreenSlice.actions

export const selectAppointments = (state: RootState) =>
  state.appointmentScreen.appointments

export const selectDate = (state: RootState) => state.appointmentScreen.date

export const selectRawDate = (state: RootState) => {
  const date = state.appointmentScreen.date
  if (!date) return undefined

  return new Date(date)
}

export const selectFormattedDate = (state: RootState) => {
  const date = state.appointmentScreen.date
  const language = state.app.language

  if (!date) return ''

  return DateTime.fromISO(date).toFormat('cccc d LLL', {
    locale: getLocale(language),
  })
}

export const selectRawUpcomingAppointmentsDate = (state: RootState) => {
  const date = state.appointmentScreen.upcomingAppointmentsDate
  if (!date) return undefined

  return new Date(date)
}

export const selectSummaryFormattedDate = (state: RootState) => {
  const date = state.appointmentScreen.date
  const startTime = state.appointmentScreen.selectedAppointment?.startTime
  const language = state.app.language
  if (!date || !startTime) return ''

  const clockString = language.includes('sv') ? 'klockan' : 'at'
  const prefixDay = language.includes('sv') ? ' den' : 'the'

  return (
    capitalizeFirstLetter(
      DateTime.fromISO(date).toFormat(
        `cccc '${prefixDay}' d LLLL, '${clockString}' `,
        {
          locale: getLocale(state.app.language),
        }
      )
    ) + DateTime.fromISO(startTime).toFormat('HH:mm')
  )
}

export const selectSelectedAppointment = (state: RootState) =>
  state.appointmentScreen.selectedAppointment

export const getIsPending = (state: RootState) =>
  state.appointmentScreen.pending

export const appointmentScreenReducer = AppointmentScreenSlice.reducer
